import { FC, Suspense, useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Channel, AdditionalChannelTabs } from '@/models';
import { AppRoutes } from '@/utils';
import { InteractionChannelFiltersProvider } from '@/context/ChanelFocus/AdditionalFiltersContext';
import { useChannelFocusStore } from '@/pages/ChannelFocus/store/useChannelFocusStore';
import { useRegisterSecondarySegmentFilters } from '@/pages/ChannelFocus/hooks/useRegisterSecondarySegmentFilters';
import { LoadingSpinnerFallback } from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import ChannelFocusNavigation from './ChannelFocusNavigation';

import styles from './ChannelFocusLayout.module.css';
import PageBanner from '../Layout/PageBanner/PageBanner';
import { useActiveDisclaimer } from '../Header/components/Disclaimer/hooks/useActiveDisclaimer';

export interface ChannelFocusLayoutReturn {
  channel: Channel | AdditionalChannelTabs.ALL_CHANNEL | undefined;
}

const ChannelFocusLayout: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const Disclaimer = useActiveDisclaimer();

  const channel = useChannelFocusStore(state => state.channel);
  const setChannel = useChannelFocusStore(state => state.setChannel);

  useRegisterSecondarySegmentFilters(channel);

  const currentChannel = useMemo(() => {
    const pathname = location.pathname;
    if (pathname === AppRoutes.CHANNEL_ALL_CHANNELS) return AdditionalChannelTabs.ALL_CHANNEL;
    if (pathname === AppRoutes.CHANNEL_FOCUS_1_TO_1) return Channel.VISITS_1_TO_1;
    if (pathname === AppRoutes.CHANNEL_FOCUS_EMAIL) return Channel.EMAIL;
    if (pathname === AppRoutes.CHANNEL_FOCUS_EVENT) return Channel.EVENT;
    if (pathname === AppRoutes.CHANNEL_FOCUS_WEBSITE) return Channel.WEBSITE;
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (currentChannel) {
      setChannel(currentChannel as Channel);
    }
  }, [currentChannel, setChannel]);

  if (!channel || !currentChannel || channel !== currentChannel) {
    return null;
  }

  return (
    <InteractionChannelFiltersProvider channel={currentChannel}>
      <PageBanner.Title>{t('header.navigation.channelFocus')}</PageBanner.Title>
      {Disclaimer && (
        <PageBanner.Note>
          <Disclaimer />
        </PageBanner.Note>
      )}
      <div className={styles.container}>
        <ChannelFocusNavigation activeChannel={currentChannel}>
          <Suspense fallback={<LoadingSpinnerFallback />}>
            <Outlet
              context={{
                channel: currentChannel,
              }}
            />
          </Suspense>
        </ChannelFocusNavigation>
      </div>
    </InteractionChannelFiltersProvider>
  );
};

export default ChannelFocusLayout;
